import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { cancelSecuredSignup, fetchMySignups, payCartAtDesk } from "../../utility/submitSignup";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import {
  AlertBox,
  Badge,
  Card,
  PrimaryButton,
  SecondaryButton,
  formatCurrency,
  formatSquadInfo,
} from "../UI/DesignStandards";
import {
  advertisedLineTotal,
  isDueCartLine,
  openCartLinesFromMySignups,
  tournamentSignupToCartLine,
} from "../../utility/signupCart";
import {
  canSelfServeCancelEntry,
  requiredCancelCompanionNote,
  requiredCancelNeedsCardRefund,
} from "../../utility/signupAvailability";
import {
  lineAmountsCents,
  quoteDollars,
  quoteSignupPayment,
} from "../../utility/signupPaymentQuote";
import PayNowPanel from "./PayNowPanel";
import SignupAgreementNote from "./SignupAgreementNote";
import {
  confirmSignupCheckout,
  getPayNowStatus,
} from "../../utility/signupPayments";
import { deskCloseoutOffered, readPayNowStatusPayload } from "../../utility/payNowStatus";
import { fetchTournamentSignupSettings } from "../../utility/signupSettings";
import { refreshDueSignupCount } from "../../hooks/useDueSignupCount";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";

const lineStatusBadge = (line) => {
  if (line.waitlisted) {
    return {
      label: line.waitlistPosition
        ? `Waitlist · position ${line.waitlistPosition}`
        : "Waitlist",
      variant: "warning",
    };
  }
  if (line.paymentStatus === "paid" || line.paymentStatus === "walk_up") {
    return { label: "Paid", variant: "success" };
  }
  if (line.paymentStatus === "held") {
    return { label: "Held", variant: "default" };
  }
  return { label: "Due", variant: "primary" };
};

const QuoteRow = ({ label, amountCents, emphasize = false }) => (
  <div
    className={`flex justify-between items-baseline gap-4 ${
      emphasize
        ? "font-display font-bold text-base mt-3 pt-3 border-t border-brand-charcoal/10"
        : "text-sm text-brand-charcoal"
    }`}
  >
    <span className={emphasize ? "" : "text-brand-muted"}>{label}</span>
    <span className="font-score tabular-nums shrink-0">
      {formatCurrency(quoteDollars(amountCents))}
    </span>
  </div>
);

const MetaRow = ({ label, children }) => (
  <div className="grid grid-cols-[6.5rem_1fr] sm:grid-cols-[7.5rem_1fr] gap-x-3 text-sm">
    <dt className="text-brand-muted">{label}</dt>
    <dd className="text-brand-charcoal min-w-0">{children}</dd>
  </div>
);

const CartLine = ({
  line,
  removing,
  onRemove,
  readOnly = false,
  canRemove = false,
  companionNote = "",
}) => {
  const status = lineStatusBadge(line);
  const amounts = lineAmountsCents(line);
  const bowlers = [line.primaryBowler, ...(line.teammates || [])]
    .filter(Boolean)
    .map((bowler) => bowler.name)
    .filter(Boolean);
  const bowlingCenter = line.squad?.bowlingCenters?.centerName;

  return (
    <li className="p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-display font-bold text-brand-charcoal tracking-tight">
            {line.event?.eventName || "Entry"}
          </h2>
          <div className="mt-2">
            <Badge variant={status.variant}>{status.label}</Badge>
          </div>
        </div>
        <div className="font-score text-lg font-display font-bold text-brand-charcoal tabular-nums shrink-0">
          {formatCurrency(advertisedLineTotal(line))}
        </div>
      </div>

      <dl className="mt-4 space-y-1.5">
        <MetaRow label="Squad">
          {formatSquadInfo(line.squad?.squadDate, line.squad?.squadTime)}
        </MetaRow>
        {bowlingCenter ? (
          <MetaRow label="Bowling center">{bowlingCenter}</MetaRow>
        ) : null}
        <MetaRow label={bowlers.length === 1 ? "Bowler" : "Bowlers"}>
          {bowlers.length ? bowlers.join(", ") : "—"}
        </MetaRow>
        {line.heldUntil &&
        (line.status === "held" || line.paymentStatus === "held") ? (
          <MetaRow label="Hold ends">
            {new Date(line.heldUntil).toLocaleString()}
          </MetaRow>
        ) : null}
        {amounts.entryCents > 0 ? (
          <MetaRow label="Entry">
            <span className="font-score tabular-nums">
              {formatCurrency(quoteDollars(amounts.entryCents))}
            </span>
          </MetaRow>
        ) : null}
        {amounts.sidepotCents > 0 ? (
          <MetaRow label="Sidepots">
            <span className="font-score tabular-nums">
              {formatCurrency(quoteDollars(amounts.sidepotCents))}
            </span>
          </MetaRow>
        ) : null}
        {amounts.bracketCents > 0 ? (
          <MetaRow label="Brackets">
            <span className="font-score tabular-nums">
              {formatCurrency(quoteDollars(amounts.bracketCents))}
            </span>
          </MetaRow>
        ) : null}
      </dl>

      {canRemove && !readOnly ? (
        <button
          type="button"
          onClick={() => onRemove(line.id)}
          disabled={removing}
          className="mt-4 text-sm text-brand-muted hover:text-red-800 disabled:opacity-50"
        >
          {removing ? "Removing…" : "Remove"}
        </button>
      ) : null}
      {companionNote && !readOnly ? (
        <p className="mt-4 text-sm text-brand-muted">{companionNote}</p>
      ) : null}
    </li>
  );
};

const entryCountLabel = (count) =>
  `${count} ${count === 1 ? "entry" : "entries"}`;

const SignupCart = () => {
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [cart, setCart] = useState({ lines: [] });
  const [groupLines, setGroupLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [cardPaymentsOn, setCardPaymentsOn] = useState(null);
  const [paymentRequired, setPaymentRequired] = useState(null);
  const [payStatusError, setPayStatusError] = useState(null);
  const [closingDesk, setClosingDesk] = useState(false);
  const [payResult, setPayResult] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const cartLinesRef = useRef([]);
  cartLinesRef.current = cart.lines;

  const receiptLinesFromIds = useCallback(
    async (signupIds) => {
      if (!signupIds?.length) return [];
      const wanted = new Set(signupIds);
      const data = await fetchMySignups();
      return (data?.signups || data?.activeSignups || [])
        .map(tournamentSignupToCartLine)
        .filter(
          (line) => wanted.has(line.id) && line.tournamentId === tournamentId
        )
        .map((line) => ({ ...line, paymentStatus: "paid" }));
    },
    [tournamentId]
  );

  const loadCart = useCallback(async () => {
    if (!user?.id) {
      setCart({ lines: [] });
      setGroupLines([]);
      setLoading(false);
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMySignups();
      const lines = openCartLinesFromMySignups(data, tournamentId);
      const grouped = (data?.signups || data?.activeSignups || [])
        .map(tournamentSignupToCartLine)
        .filter((line) => !tournamentId || line.tournamentId === tournamentId);
      setGroupLines(grouped);
      setCart({ lines });
      return lines;
    } catch (loadError) {
      setError(loadError.message || "Could not load the cart.");
      setCart({ lines: [] });
      setGroupLines([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [tournamentId, user?.id]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  useEffect(() => {
    if (!user?.id || !tournamentId) return undefined;
    const paidFlag = searchParams.get("paid");
    const redirectStatus = searchParams.get("redirect_status");
    const rawSessionId = searchParams.get("session_id") || "";
    const sessionId = rawSessionId.startsWith("cs_") ? rawSessionId : "";
    const isReturn =
      paidFlag === "1" ||
      Boolean(sessionId) ||
      redirectStatus === "succeeded" ||
      redirectStatus === "failed";
    if (!isReturn) return undefined;

    const stripReturnParams = () => {
      const next = new URLSearchParams(searchParams);
      ["paid", "redirect_status", "session_id"].forEach((key) =>
        next.delete(key)
      );
      setSearchParams(next, { replace: true });
    };

    if (redirectStatus === "failed") {
      setPayResult("failed");
      stripReturnParams();
      return undefined;
    }

    let cancelled = false;
    const finishReturn = async () => {
      const dueSnapshot = cartLinesRef.current.filter(isDueCartLine);
      let confirmation = null;
      try {
        confirmation = await confirmSignupCheckout({
          tournamentId,
          sessionId,
        });
      } catch (_) {
        confirmation = null;
      }
      if (cancelled) return;
      const remaining = await loadCart();
      if (cancelled) return;
      const paid = Boolean(confirmation?.paid);
      const stillDue = (remaining || []).some(isDueCartLine);
      if (paid || !stillDue) {
        let lines = confirmation?.signupIds?.length
          ? await receiptLinesFromIds(confirmation.signupIds)
          : [];
        if (!lines.length && dueSnapshot.length) {
          lines = dueSnapshot.map((line) => ({
            ...line,
            paymentStatus: "paid",
          }));
        }
        setReceipt({
          lines,
          amountCents: confirmation?.amountCents || null,
        });
        setPayResult("paid");
      } else {
        setPayResult("pending");
      }
      refreshDueSignupCount();
      stripReturnParams();
    };
    finishReturn();
    return () => {
      cancelled = true;
    };
  }, [
    loadCart,
    receiptLinesFromIds,
    searchParams,
    setSearchParams,
    tournamentId,
    user?.id,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      if (!tournamentId) {
        setCardPaymentsOn(null);
        setPaymentRequired(false);
        setPayStatusError(null);
        return;
      }
      try {
        const [payResult, settings] = await Promise.all([
          getPayNowStatus(tournamentId).then(
            (payload) => readPayNowStatusPayload(payload),
            (error) => readPayNowStatusPayload(null, error)
          ),
          fetchTournamentSignupSettings(tournamentId),
        ]);
        if (cancelled) return;
        setPaymentRequired(Boolean(settings.paymentRequiredToHold));
        if (payResult.known) {
          setCardPaymentsOn(payResult.payNowEnabled);
          setPayStatusError(null);
        } else {
          setCardPaymentsOn(null);
          setPayStatusError(
            payResult.error || "Could not check card payments."
          );
        }
      } catch (_) {
        if (!cancelled) {
          setCardPaymentsOn(null);
          setPaymentRequired(false);
          setPayStatusError("Could not check card payments.");
        }
      }
    };
    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  const quote = quoteSignupPayment(cart.lines, {
    tournamentId,
    userId: user?.id || null,
    email: user?.email || null,
  });
  const hasDue = cart.lines.some(isDueCartLine);
  const settingsKnown =
    paymentRequired !== null &&
    (cardPaymentsOn !== null || Boolean(payStatusError));
  const showPayNow = cardPaymentsOn === true;
  const showDeskButton =
    settingsKnown &&
    cart.lines.length > 0 &&
    deskCloseoutOffered({
      payNowEnabled: cardPaymentsOn,
      paymentRequiredToHold: paymentRequired,
    });
  const showReceipt = payResult === "paid" && Boolean(receipt);
  const returningFromPay =
    searchParams.get("paid") === "1" ||
    (searchParams.get("session_id") || "").startsWith("cs_") ||
    searchParams.get("redirect_status") === "succeeded";

  const handleRemove = async (lineId) => {
    setRemovingId(lineId);
    setError(null);
    try {
      const line = cart.lines.find((entry) => entry.id === lineId);
      await cancelSecuredSignup({
        signupId: lineId,
        paymentStatus: line?.paymentStatus,
        cardRefund: requiredCancelNeedsCardRefund(line, groupLines),
      });
      await loadCart();
    } catch (cancelErr) {
      setError(cancelErr.message || "Could not remove this entry.");
    } finally {
      setRemovingId(null);
    }
  };

  const handlePayAtDesk = async () => {
    setClosingDesk(true);
    setError(null);
    try {
      await payCartAtDesk({ tournamentId });
      await loadCart();
      refreshDueSignupCount();
      navigate("/signup/dashboard?tab=due");
    } catch (deskError) {
      setError(deskError.message || "Could not close Cart.");
    } finally {
      setClosingDesk(false);
    }
  };

  const handleInPagePaid = async (status, confirmation) => {
    const snapshot = cart.lines.map((line) => ({
      ...line,
      paymentStatus: "paid",
    }));
    const remaining = await loadCart();
    const stillDue = (remaining || []).some(isDueCartLine);
    if (status === "paid" || !stillDue) {
      setReceipt({
        lines: snapshot,
        amountCents: confirmation?.amountCents || quote.amountCents,
      });
      setPayResult("paid");
    } else {
      setPayResult("pending");
    }
    refreshDueSignupCount();
  };

  return (
    <>
      <div className="flex justify-center mb-3 sm:mb-4 px-4 sm:px-0">
        <Breadcrumb
          items={[
            { label: "Tournaments", path: "/tournaments" },
            {
              label: "Tournament",
              path: tournamentPath(tournamentSlug),
            },
            { label: "Signups", path: tournamentPath(tournamentSlug, `/signups`) },
            { label: showReceipt ? "Paid" : "Cart" },
          ]}
        />
      </div>

      <div className="space-y-6">
        {payResult === "pending" ? (
          <AlertBox
            type="warning"
            title="Payment is still processing."
            message="If status still says Due, wait a moment and refresh."
          />
        ) : payResult === "failed" ? (
          <AlertBox
            type="error"
            title="Please fix this"
            message="Card was not charged."
          />
        ) : null}

        {error ? (
          <AlertBox type="error" title="Please fix this" message={error} />
        ) : null}

        {showReceipt ? (
          <div className="lg:grid lg:grid-cols-3 lg:gap-8 lg:items-start">
            <aside className="order-last lg:order-none lg:col-start-3 lg:row-start-1 mt-6 lg:mt-0 lg:sticky lg:top-3">
              <Card className="overflow-hidden" padding="">
                <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
                  <h2 className="font-display font-bold text-lg text-brand-cream tracking-tight">
                    Paid
                  </h2>
                  <p className="text-sm text-brand-mist mt-1">
                    {receipt.lines.length
                      ? entryCountLabel(receipt.lines.length)
                      : "This charge is complete."}
                  </p>
                </div>
                <div className="p-6 space-y-4">
                  {receipt.amountCents != null ? (
                    <QuoteRow
                      label="Card total"
                      amountCents={receipt.amountCents}
                      emphasize
                    />
                  ) : null}
                  <p className="text-sm text-brand-muted leading-relaxed">
                    These entries are on My Signups.
                  </p>
                  <PrimaryButton
                    className="w-full py-3"
                    onClick={() => navigate("/signup/dashboard?tab=upcoming")}
                  >
                    View My Signups
                  </PrimaryButton>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(tournamentPath(tournamentSlug, `/signups`))
                    }
                    className="w-full text-sm text-brand-gold-ink hover:underline"
                  >
                    Add another event
                  </button>
                </div>
              </Card>
            </aside>
            <div className="lg:col-span-2 lg:col-start-1 lg:row-start-1">
              <div className="card-board overflow-hidden">
                <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
                  <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                    Paid
                  </h1>
                  <p className="text-sm text-brand-mist mt-1">
                    {receipt.lines.length
                      ? `${entryCountLabel(receipt.lines.length)} on this charge.`
                      : "This charge is complete."}
                  </p>
                </div>
                {receipt.lines.length ? (
                  <ul className="divide-y divide-brand-charcoal/10">
                    {receipt.lines.map((line) => (
                      <CartLine key={line.id} line={line} readOnly />
                    ))}
                  </ul>
                ) : (
                  <div className="p-6">
                    <p className="text-brand-muted">
                      Open My Signups to see the entries from this charge.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : returningFromPay || (loading && cart.lines.length === 0) ? (
          <div className="card-board overflow-hidden">
            <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
              <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                Cart
              </h1>
              <p className="text-sm text-brand-mist mt-1">Loading cart…</p>
            </div>
            <div className="p-6">
              <p className="text-brand-muted">Loading cart…</p>
            </div>
          </div>
        ) : cart.lines.length > 0 ? (
          <div className="lg:grid lg:grid-cols-3 lg:gap-8 lg:items-start">
            <aside className="order-last lg:order-none lg:col-start-3 lg:row-start-1 mt-6 lg:mt-0 lg:sticky lg:top-3">
              <Card
                className="overflow-hidden lg:max-h-[calc(100vh-1.5rem)] lg:overflow-y-auto"
                padding=""
              >
                <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
                  <h2 className="font-display font-bold text-lg text-brand-cream tracking-tight">
                    {!settingsKnown || payStatusError
                      ? "Cart"
                      : showPayNow
                        ? "Pay now"
                        : "Due"}
                  </h2>
                  <p className="text-sm text-brand-mist mt-1">
                    {hasDue
                      ? `${cart.lines.filter(isDueCartLine).length} due`
                      : "Nothing due"}
                  </p>
                </div>
                <div className="p-6">
                  {!settingsKnown ? (
                    <p className="text-sm text-brand-muted">
                      Checking payment settings…
                    </p>
                  ) : payStatusError && hasDue ? (
                    <div>
                      <p className="text-sm text-red-800">{payStatusError}</p>
                      <p className="text-sm text-brand-muted mt-3">
                        Pay at the desk, or try Cart again.
                      </p>
                    </div>
                  ) : cardPaymentsOn === false && hasDue ? (
                    <div>
                      {quote.lines.map((line) => (
                        <QuoteRow
                          key={line.signupId || line.label}
                          label={line.label}
                          amountCents={line.advertisedCents}
                        />
                      ))}
                      <QuoteRow
                        label="Due at the desk"
                        amountCents={quote.deskDueCents}
                        emphasize
                      />
                      <p className="text-xs text-brand-muted mt-3 leading-relaxed">
                        The desk total is the advertised entry, sidepots, and
                        brackets. No platform fee.
                      </p>
                      <p className="text-sm text-brand-muted mt-3">
                        Pay at the desk. Pay now is available after the
                        organization turns on Card payments.
                      </p>
                    </div>
                  ) : hasDue && showPayNow ? (
                    <PayNowPanel
                      tournamentId={tournamentId}
                      lines={cart.lines}
                      user={user}
                      onPaid={handleInPagePaid}
                      payNowEnabled={cardPaymentsOn === true}
                      statusKnown={cardPaymentsOn !== null}
                      hideTitle
                    />
                  ) : (
                    <p className="text-sm text-brand-charcoal">
                      {cart.lines.some((line) => line.waitlisted)
                        ? "Waitlist does not hold a squad spot."
                        : "Nothing due for Pay now."}
                    </p>
                  )}
                  {settingsKnown && hasDue && !showPayNow ? (
                    <div className="mt-4">
                      <SignupAgreementNote />
                    </div>
                  ) : null}
                  {showDeskButton ? (
                    <SecondaryButton
                      className="w-full mt-4 py-3"
                      onClick={handlePayAtDesk}
                      disabled={closingDesk}
                    >
                      {closingDesk ? "Closing Cart…" : "Pay at the desk"}
                    </SecondaryButton>
                  ) : null}
                </div>
              </Card>
            </aside>

            <div className="lg:col-span-2 lg:col-start-1 lg:row-start-1">
              <div className="card-board overflow-hidden">
                <div className="bg-brand-charcoal text-brand-cream px-6 py-4 flex items-start justify-between gap-4">
                  <div>
                    <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                      Cart
                    </h1>
                    <p className="text-sm text-brand-mist mt-1">
                      {entryCountLabel(cart.lines.length)} in this order.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(tournamentPath(tournamentSlug, `/signups`))
                    }
                    className="shrink-0 text-sm text-brand-gold hover:text-brand-cream mt-1"
                  >
                    Add another event
                  </button>
                </div>
                <ul className="divide-y divide-brand-charcoal/10">
                  {cart.lines.map((line) => (
                    <CartLine
                      key={line.id}
                      line={line}
                      removing={removingId === line.id}
                      onRemove={handleRemove}
                      canRemove={canSelfServeCancelEntry(line, groupLines)}
                      companionNote={requiredCancelCompanionNote(line, groupLines)}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="card-board overflow-hidden">
            <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
              <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                Cart
              </h1>
              <p className="text-sm text-brand-mist mt-1">
                {loading
                  ? "Loading cart…"
                  : !user
                    ? "Sign in to see your entries."
                    : "This order is empty."}
              </p>
            </div>
            <div className="p-6">
              {loading ? (
                <p className="text-brand-muted">Loading cart…</p>
              ) : !user ? (
                <p className="text-brand-muted">
                  Sign in to see entries on this tournament.
                </p>
              ) : (
                <p className="text-brand-muted">
                  Nothing in this order.{" "}
                  <Link
                    className="underline text-brand-gold-ink"
                    to={tournamentPath(tournamentSlug, `/signups`)}
                  >
                    Add an event
                  </Link>
                  .
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SignupCart;
